import { createCaravanDb } from '../data/local/db'
import { PracticalRepository, type Anecdote } from '../data/local/practicalRepository'
import { NotificationRepository } from '../data/local/notificationRepository'
import { createDemoAccessGateway } from '../data/remote/demoGateway'
import { DemoSyncGateway } from '../data/remote/demoSyncGateway'
import type { AccessGateway, SessionView } from '../domain/access'
import type { LocalEntity, SyncState } from '../domain/sync'
import { tripDateKey } from '../domain/tripTime'
import { runtimeProfile } from '../config/runtime'
import { sessionStore } from '../features/access/sessionStore'
import { ConnectivityMonitor } from './connectivity'
import { lockProtectedCache } from './revocation'
import type { RevealService } from './revealService'
import { SyncEngine } from './syncEngine'
import { SyncScheduler } from './syncScheduler'

export const caravanDb = createCaravanDb()
export const practicalRepository = new PracticalRepository(caravanDb)
export const notificationRepository = new NotificationRepository(caravanDb)
const demoSyncOptions = {
  connectivityCheck: async () => {
    if (!navigator.onLine) return false
    try {
      await fetch(`/__caravan_connectivity?at=${String(Date.now())}`, {
        method: 'HEAD',
        cache: 'no-store',
      })
      return true
    } catch {
      return false
    }
  },
}
let syncEngine: SyncEngine | null = null
let runtimeAccessGateway: AccessGateway | null = null
let sharedRuntime: Promise<{ accessGateway: AccessGateway; engine: SyncEngine }> | null = null
export let revealService: RevealService | null = null
export const syncRuntimeMode = runtimeProfile.mode

if (runtimeProfile.mode === 'demo') {
  runtimeAccessGateway = createDemoAccessGateway()
  syncEngine = new SyncEngine(caravanDb, new DemoSyncGateway(demoSyncOptions), { mode: 'demo' })
}
const connectivity = new ConnectivityMonitor()

const changeEvent = 'caravan:sync-state-changed'
let initialized: Promise<void> | null = null
let connectivityStarted = false
let scheduler: SyncScheduler | null = null

export type SyncSnapshot = {
  mode: 'demo' | 'supabase'
  online: boolean
  counts: Record<SyncState, number>
  latestAnecdotes: LocalEntity[]
  oldestActionableAt: number | null
  lastSuccessfulSyncAt: string | null
}

async function requireSyncEngine(): Promise<SyncEngine> {
  if (syncEngine) return syncEngine
  return (await loadSharedRuntime()).engine
}

export function loadRuntimeAccessGateway(): Promise<AccessGateway> {
  if (runtimeAccessGateway) return Promise.resolve(runtimeAccessGateway)
  return loadSharedRuntime().then((runtime) => runtime.accessGateway)
}

export function getImmediateRuntimeAccessGateway(): AccessGateway | null {
  return runtimeAccessGateway
}

function loadSharedRuntime(): Promise<{ accessGateway: AccessGateway; engine: SyncEngine }> {
  sharedRuntime ??= Promise.all([
    import('../data/remote/supabaseClient'),
    import('./runtimeComposition'),
  ]).then(([clientModule, compositionModule]) => {
    const client = clientModule.getSupabaseClient()
    if (!client || runtimeProfile.mode !== 'supabase') {
      throw new Error('La configuration Supabase partagée est indisponible.')
    }
    const dependencies = compositionModule.createRuntimeDependencies({
      profile: runtimeProfile,
      db: caravanDb,
      client,
    })
    runtimeAccessGateway = dependencies.accessGateway
    revealService = dependencies.revealService
    const engine = new SyncEngine(caravanDb, dependencies.syncGateway, {
      mode: 'supabase',
      onRevoked: async () => {
        sessionStore.getState().markRevoked()
        await lockProtectedCache(caravanDb)
      },
    })
    syncEngine = engine
    return { accessGateway: dependencies.accessGateway, engine }
  })
  return sharedRuntime
}

function announceChange(): void {
  window.dispatchEvent(new CustomEvent(changeEvent))
}

export function initializeSyncRuntime(): Promise<void> {
  initialized ??= (async () => {
    const engine = await requireSyncEngine()
    await engine.recoverInterruptedWork()
    if (!connectivityStarted) {
      connectivityStarted = true
      connectivity.subscribe(() => {
        announceChange()
      })
    }
    if (navigator.onLine) await engine.syncOnce()
    scheduler ??= new SyncScheduler({ runner: engine, onSettled: announceChange })
    scheduler.start()
    announceChange()
  })()
  return initialized
}

export async function createLocalAnecdote(
  text: string,
  session: SessionView,
  detail: { place?: string; productDay?: string } = {},
): Promise<Anecdote> {
  const anecdote = await practicalRepository.createAnecdote({
    tripId: session.tripId,
    memberId: session.memberId,
    authorDisplayName: session.displayName,
    productDay: detail.productDay ?? tripDateKey(new Date()),
    text,
    place: detail.place ?? '',
    coordinates: null,
  })
  announceChange()
  if (navigator.onLine) void syncNow()
  return anecdote
}

export async function saveLocalConversionRate(rate: number, session: SessionView): Promise<void> {
  await practicalRepository.saveConversionRate(session.memberId, session.tripId, rate)
  announceChange()
  if (navigator.onLine) void syncNow()
}

export async function syncNow(): Promise<void> {
  const engine = await requireSyncEngine()
  await engine.retryNow()
  announceChange()
}

export async function readSyncSnapshot(): Promise<SyncSnapshot> {
  const rows = await caravanDb.outbox.toArray()
  const counts: Record<SyncState, number> = {
    pending: 0,
    syncing: 0,
    synced: 0,
    failed: 0,
    conflict: 0,
    recovery: 0,
  }
  for (const row of rows) counts[row.state] += 1
  const actionable = rows
    .filter((row) => ['pending', 'failed', 'conflict', 'recovery'].includes(row.state))
    .map((row) => Date.parse(row.createdAtDevice))
    .filter(Number.isFinite)
  const anecdoteRows = await caravanDb.entities
    .where('entityType')
    .equals('anecdote')
    .filter((entity) => entity.deletedAt === null)
    .sortBy('updatedAtDevice')
  const latestAnecdotes = anecdoteRows.reverse()
  const setting = await caravanDb.settings.get('sync')
  return {
    mode: syncRuntimeMode,
    online: navigator.onLine,
    counts,
    latestAnecdotes: latestAnecdotes.slice(0, 5),
    oldestActionableAt: actionable.length > 0 ? Math.min(...actionable) : null,
    lastSuccessfulSyncAt: setting?.lastSuccessfulSyncAt ?? null,
  }
}

export function subscribeSyncRuntime(listener: () => void): () => void {
  const handler = () => listener()
  window.addEventListener(changeEvent, handler)
  window.addEventListener('online', handler)
  window.addEventListener('offline', handler)
  return () => {
    window.removeEventListener(changeEvent, handler)
    window.removeEventListener('online', handler)
    window.removeEventListener('offline', handler)
  }
}
